import { Media } from '../types/media.types';
import type { RcFile } from 'antd/es/upload';
import { UploadFile } from 'antd/es/upload/interface';

interface MediaConversionOptions {
  url: string;
  originalName: string;
  mimeType: string;
}

export interface MediaUploadConfig {
  maxFiles?: number;
  allowedFileTypes?: string[];
  maxFileSize?: number; // in bytes
  multiple?: boolean;
  maxZipSize?: number; // in bytes - max size for zip files
}

export interface MediaUploadResult {
  files: RcFile[];
  fileList: UploadFile[];
  error?: string;
}

/**
 * Converts a single file from URL to File object
 */
export const convertUrlToFile = async (options: MediaConversionOptions): Promise<File | null> => {
  try {
    const response = await fetch(options.url);
    if (!response.ok) {
      console.error('Failed to fetch file:', response.status, response.statusText);
      return null;
    }
    const blob = await response.blob();
    return new File([blob], options.originalName, { type: options.mimeType });
  } catch (error) {
    console.error('Error converting file:', error);
    return null;
  }
};

/**
 * Converts multiple files from URLs to File objects
 */
export const convertUrlsToFiles = async (files: Media[]): Promise<File[]> => {
  const convertedFiles = await Promise.all(
    files.map(async (file) => {
      return convertUrlToFile({
        url: file.url,
        originalName: file.originalName,
        mimeType: file.mimeType,
      });
    })
  );
  return convertedFiles.filter((file): file is File => file !== null);
};

const validateFile = (file: RcFile, config: MediaUploadConfig): string | null => {
  if (config.allowedFileTypes) {
    const isAllowed = config.allowedFileTypes.some((allowedType) => {
      // Handle wildcard patterns like "image/*"
      if (allowedType.endsWith('/*')) {
        const baseType = allowedType.slice(0, -2);
        return file.type.startsWith(baseType + '/');
      }
      // Handle exact matches
      return file.type === allowedType;
    });

    if (!isAllowed) {
      return `File type ${file.type} is not allowed`;
    }
  }

  if (config.maxFileSize && file.size > config.maxFileSize) {
    return `File size exceeds maximum limit of ${config.maxFileSize / (1024 * 1024)}MB`;
  }

  return null;
};

const createFileList = (files: RcFile[]): UploadFile[] => {
  return files.map((file, index) => ({
    uid: file.uid || `-${index + 1}`,
    name: file.name,
    status: 'done',
    url: URL.createObjectURL(file),
    originFileObj: file,
  }));
};

/**
 * Check if a file is a zip file
 */
export const isZipFile = (file: RcFile): boolean => {
  const zipMimeTypes = [
    'application/zip',
    'application/x-zip-compressed',
    'application/x-zip',
    'application/octet-stream', // Sometimes zip files are detected as this
  ];

  const zipExtensions = ['.zip', '.rar', '.7z', '.tar', '.gz'];

  // Check MIME type
  if (zipMimeTypes.includes(file.type)) {
    return true;
  }

  // Check file extension as fallback
  const fileName = file.name.toLowerCase();
  return zipExtensions.some((ext) => fileName.endsWith(ext));
};

export const handleFileUpload = (
  fileList: UploadFile[],
  currentFiles: RcFile[] | null,
  config: MediaUploadConfig
): MediaUploadResult => {
  try {
    // Extract new files from fileList
    const newFiles = fileList
      .map((file) => file.originFileObj)
      .filter((file): file is RcFile => file instanceof File);

    // Handle file removal
    if (fileList.length < (currentFiles?.length || 0)) {
      const remainingFiles = newFiles;
      return {
        files: remainingFiles,
        fileList: createFileList(remainingFiles),
      };
    }

    // Validate files
    for (const file of newFiles) {
      const error = validateFile(file, config);
      if (error) {
        return {
          files: [],
          fileList: [],
          error,
        };
      }
    }

    // Check max files limit
    if (config.maxFiles && newFiles.length > config.maxFiles) {
      return {
        files: [],
        fileList: [],
        error: `Maximum ${config.maxFiles} file(s) allowed`,
      };
    }

    // Handle single file case
    if (!config.multiple && newFiles.length > 1) {
      return {
        files: [newFiles[0]],
        fileList: createFileList([newFiles[0]]),
      };
    }

    return {
      files: newFiles,
      fileList: createFileList(newFiles),
    };
  } catch {
    return {
      files: [],
      fileList: [],
      error: 'Error processing files',
    };
  }
};
